import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ListaDeMidiasDto } from "@base/contracts";
import { useAuth } from "../../../shared/auth/auth.contexto";
import type { FiltroListarMidias } from "../infrastructure/midias.gateway";
import { useMidiasGateway, useMidiasGatewayOpcional } from "./midias.contexto";

function chaveDaLista(
  tenantId: string | undefined,
  filtro?: FiltroListarMidias,
): readonly unknown[] {
  return [
    "midias",
    "lista",
    tenantId ?? "",
    filtro?.tipo ?? "",
    (filtro?.plataformas ?? []).join(","),
    filtro?.q ?? "",
    filtro?.tipoArquivo ?? "",
    filtro?.origem ?? "",
    filtro?.soFavoritas ? "1" : "",
    filtro?.ordenar ?? "",
  ];
}

export function useTemMidias(): boolean {
  const { sessao } = useAuth();
  return Boolean(sessao?.escopos.includes("midias:ler"));
}

export function usePodeEscreverMidias(): boolean {
  const { sessao } = useAuth();
  return Boolean(sessao?.escopos.includes("midias:escrever"));
}

export function useListarMidias(filtro?: FiltroListarMidias) {
  const gateway = useMidiasGateway();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: chaveDaLista(sessao?.tenantId, filtro),
    queryFn: () => gateway.listar(filtro),
  });
}

export function useListarMidiasOpcional(filtro?: FiltroListarMidias) {
  const gateway = useMidiasGatewayOpcional();
  const { sessao } = useAuth();
  return useQuery({
    queryKey: chaveDaLista(sessao?.tenantId, filtro),
    queryFn: () => {
      if (!gateway) return { itens: [] };
      return gateway.listar(filtro);
    },
    enabled: Boolean(gateway),
  });
}

export function useEnviarMidias() {
  const gateway = useMidiasGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arquivos: File[]) => gateway.enviar(arquivos),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["midias"] });
    },
  });
}

export function useAtualizarFavorita() {
  const gateway = useMidiasGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entrada: { readonly id: string; readonly favorita: boolean }) =>
      gateway.marcarFavorita(entrada.id, entrada.favorita),
    onMutate: async (entrada) => {
      await qc.cancelQueries({ queryKey: ["midias"] });
      const anteriores = qc.getQueriesData<ListaDeMidiasDto>({
        queryKey: ["midias"],
      });
      qc.setQueriesData<ListaDeMidiasDto>({ queryKey: ["midias"] }, (atual) => {
        if (!atual) return atual;
        return {
          itens: atual.itens.map((item) =>
            item.id === entrada.id
              ? { ...item, favorita: entrada.favorita }
              : item,
          ),
        };
      });
      return { anteriores };
    },
    onError: (_erro, _entrada, contexto) => {
      for (const [chave, dados] of contexto?.anteriores ?? []) {
        qc.setQueryData(chave, dados);
      }
    },
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ["midias"] });
    },
  });
}

export function useExcluirMidia() {
  const gateway = useMidiasGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gateway.excluir(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["midias"] });
    },
  });
}
